<template>
<div class="app-create-new-ai-content is--video-script">
  <div class="page-main__content grid">
    <div class="app-create-new-ai-content__header col-12">
      <h3 class="app-create-new-ai-content__title">Create New Video Script</h3>
      <a class="app-create-new-ai-content__close element-button--icon element-button" 
        :href="`/projects/${project.id}/videoscripts`">
        <svg class="element-icon--x element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
      </a>
    </div>

    <ul class="app-create-new-ai-content__nav app-vertical-navigation col-12 col-sm-3">
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'basic' ? 'is--selected':'', isCreating ? 'is--static' : '']" @click="updateSubscreen('basic')">
        <svg class="element-icon--check-circle element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-check-circle"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Basic details</h4>
      </li>
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'topics' ? 'is--selected':'', isCreating ? 'is--static' : '']" @click="updateSubscreen('topics')">
        <svg class="element-icon--upload-square element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-upload-square"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Select your golden source</h4>
      </li>
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'review' ? 'is--selected':'', isCreating ? 'is--static' : '']" @click="updateSubscreen('review')">
        <svg class="element-icon--task-list element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-task-list"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Final details</h4>
      </li>
    </ul>

    <div class="app-create-new-ai-content__info col-12 col-sm-9">
      <app-error
        v-if="hasErrors"
        class="is--inline"
        v-bind="{ errors, message: errorMessage }" />
      <video-script-app-basic-details
        v-if="subScreen === 'basic'"
        :videoScript="videoScript"
        :saveTrigger="saveTrigger"
        :subScreen="subScreen"
        :disabledButton="disabled"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :errors="errors"
        @submissionOfData="handleSubmission($event)"
        ref="basicDetails">
      </video-script-app-basic-details>

      <ProjectSourcesSelection
        v-if="subScreen === 'topics' && !isCreating"
        :topics="topics"
        :selectedTopics="selectedTopics"
        :isVideoScript="true"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :errors="errors"
        instructions="Select which Golden Source domains and/or topics you'd like the video script to include. If none are selected, all that are listed will be used."
        @updateSelectedTopics="onTopicSelection($event)"
        @previousScreen="!hasErrors ? setSubscreen('basic'): null"
        @saveData="handleSubmission($event)">
      </ProjectSourcesSelection>

      <video-script-app-final-review
        v-if="subScreen === 'review' && !isCreating"
        :videoScript="videoScript"
        :selectedTopics="selectedTopics"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :subScreen="subScreen"
        :errors="errors"
        :disabledButton="disabled"
        :topics="topics"
        @setSubscreen="setSubscreen($event)"
        @submit="submit()">
      </video-script-app-final-review>

    </div>
  </div>
</div>
</template>

<script>
import _ from 'lodash'
import api from '../api/ai'
import publisher from '../api/publisher'
import ProjectSourcesSelection from './ProjectSourcesSelection.vue'

export default {
  components: { ProjectSourcesSelection },
  props: {
    project: {
      type: Object,
    },
    projectSource: {
      type: Object,
    },
    item: {
      type: Object,
      default: () => (null)
    },
    topics: {
      type: Array,
      default: () => ([])
    },
  },
  data() {
    return {
      videoScriptData: this.item ?? null,
      disabled: false,
      subScreen: 'basic',
      descriptionEntries: this.videoScript?.ai_preferences || {},
      saveTrigger: false,
      errorMessage: null,
      errors: []
    }
  },
  computed: {
    videoScript:{
      get() {
        return this.videoScriptData !== 'null' && this.videoScriptData !== null ? this.videoScriptData : {
          name: '',
          ai_preferences: {
            input_description: '',
            input_duration: '',
            input_structure: '',
          }
        }
      },
      set(val) {
        this.videoScriptData = val
      }
    },
    isCreating () {
      console.log('isCreating', this.videoScript)
      return !this.videoScript?.id
    },
    hasErrors () {
      return this.errorMessage || (this.errors?.message ?? this.errors?.errors?.message)
    },
    selectedTopics () {
      return this.videoScript.ai_preferences?.input_topics || []
    },
    prerequisitesMet () {
      return true
    },
  },
  methods: {
    setSubscreen(type) {
      this.subScreen = type
    },
    updateSubscreen(type) {
      if (this.isCreating || this.hasErrors)
        return

      this.saveTrigger = true
      setTimeout(() => {
        if(!this.isCreating) {
          this.subScreen = type
        } else {
          this.subScreen = 'basic'
        }
      }, 100)
    },
    onTopicSelection(event) {
      this.videoScript.ai_preferences.input_topics = event
    },

    handleSubmission(event) {
      console.log('Handling submission...', event)
      if (event.params) {
        this.save(event.subScreen, event.menuSwap, event.params)
      } else {
        this.save(event.subScreen, event.menuSwap)
      }
      if(event.entries) {
        this.descriptionEntries = event.entries
      }
    },
    async save(nextScreen = 'topics', menuSelection = null, params = {}) {
      this.disabled = true
      this.saveTrigger = false
      console.log('Saving video script...', nextScreen, this.videoScript, params)
      if (this.isCreating) {
        // Create a new video script

        const result = await publisher.createVideoScript(this.project.id, params)

        console.log(result)
        if (result.errors) {
          this.disabled = false
          this.errors = result.errors
          this.errorMessage = result.message
          return
        }

        window.location.href = `/projects/${this.project.id}/videoscripts/${result.id}/edit#topics`;
        return
      }

      console.log('Saving video script', this.videoScript)
      if (this.subScreen === 'topics') {
        params = { input_topics: this.selectedTopics }
      }

      // api call to prepare outline
      const result = await publisher.updateVideoScript(this.project.id, this.videoScript.id, params)

      console.log(result)
      if (result.errors) {
        // catch errors
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        this.errors = []
        this.errorMessage = null
        this.videoScript = result
        if (menuSelection !== 'menuSwap'){
          this.setSubscreen(nextScreen)
        }
      }
      this.disabled = false
    },
    async submit() {
      this.disabled = true
      console.log('Submitting video script...', this.videoScript, this.descriptionEntries)
      let params = this.descriptionEntries

      params.dismissAi = true // Force to only use source files

      // api call to prepare outline
      const result = await api.generateVideoScript(this.project.id, this.videoScript.id, params)
      this.saveTrigger = false
      console.log(result)
      if (result.errors) {
        // catch errors
        this.disabled = false
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        // redirect
        window.location.href = '/projects/' + this.project.id + '/videoscripts';
      }
    }
  },
  watch: {
    topics: {
      handler(newTopics) {
        const allCurrentUUIDs = newTopics.flatMap(domain => domain.topics.map(topic => topic.uuid));
        if (this.videoScript?.ai_preferences?.input_topics) {
          const filtered = this.videoScript.ai_preferences.input_topics.filter(uuid =>
            allCurrentUUIDs.includes(uuid)
          );
          if (filtered.length !== this.videoScript.ai_preferences.input_topics.length) {
            this.videoScript.ai_preferences.input_topics = filtered;
          }
        }
      },
      deep: true,
    }
  },
  created() {
    console.log('Created video script generate component', this.videoScript, this.topics);
    const allCurrentUUIDs = this.topics.flatMap(domain => domain.topics.map(topic => topic.uuid));
    if (this.videoScript?.ai_preferences?.input_topics) {
      const filtered = this.videoScript.ai_preferences.input_topics.filter(uuid =>
        allCurrentUUIDs.includes(uuid)
      );
      if (filtered.length !== this.videoScript.ai_preferences.input_topics.length) {
        this.videoScript.ai_preferences.input_topics = filtered;
      }
    }
  },
  mounted() {
    if (window.location.hash && window.location.hash.toLowerCase() === '#topics')
      this.subScreen = 'topics';
    this.descriptionEntries = this.videoScript?.ai_preferences || {};

  }
}
</script>
